import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logoutUser } from "../../actions/authentication";
import {
  clearHandheldPinLock,
  rememberHandheldEnterprise,
} from "../../helpers/handheldPin";
import {
  HANDHELD_IDLE_LOGOUT_MS,
  clearHandheldIdleLogout,
  handheldIdleDue,
  isScannerIdleSession,
  noteHandheldActivity,
  readHandheldIdle,
  shouldHandheldIdleLogout,
} from "../../helpers/handheldIdleLogout";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "click", "scroll"];

/**
 * Signs the scanner session out after 30 minutes with no scans or taps.
 * Logout lands on the gun PIN pad, not the email login.
 */
const HandheldIdleLogout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((s) => s?.currentUser?.user || {});
  const enterprise = useSelector((s) => s?.currentUser?.enterprise || {});
  const uid = user?.uid || "";
  const active =
    shouldHandheldIdleLogout(user, enterprise) &&
    isScannerIdleSession({
      pathname: location.pathname,
      search: location.search,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      screenWidth: typeof window !== "undefined" ? window.screen?.width : 0,
      screenHeight: typeof window !== "undefined" ? window.screen?.height : 0,
      innerWidth: typeof window !== "undefined" ? window.innerWidth : 0,
      innerHeight: typeof window !== "undefined" ? window.innerHeight : 0,
      devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    });

  const lastRef = useRef(Date.now());
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active || !uid) return undefined;

    const signOut = () => {
      rememberHandheldEnterprise(
        user?.enterpriseId || enterprise?.uid || enterprise?.id
      );
      clearHandheldIdleLogout();
      clearHandheldPinLock();
      dispatch(logoutUser());
    };

    const stored = readHandheldIdle(uid);
    const last = stored?.lastActivityAt ?? Date.now();
    lastRef.current = last;
    if (!stored) noteHandheldActivity(uid, last);
    if (handheldIdleDue(last, Date.now())) {
      signOut();
      return undefined;
    }

    const arm = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const remaining = Math.max(0, HANDHELD_IDLE_LOGOUT_MS - (Date.now() - lastRef.current));
      timerRef.current = setTimeout(() => {
        if (handheldIdleDue(lastRef.current, Date.now())) signOut();
        else arm();
      }, remaining);
    };
    arm();

    let throttleUntil = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now < throttleUntil) return;
      throttleUntil = now + 1000;
      lastRef.current = now;
      noteHandheldActivity(uid, now);
      arm();
    };
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (handheldIdleDue(lastRef.current, Date.now())) signOut();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, uid, dispatch, user?.enterpriseId, enterprise?.uid, enterprise?.id]);

  return null;
};

export default HandheldIdleLogout;
